import React, { useEffect, useState } from 'react'
import { LocationWrapperMini } from '../components/LocationWrapperMini'
import { Col, Row, Spin, Drawer, Modal, Rate, Button, notification } from 'antd'
import briefCase from '../assets/images/restaurant/suitcase_outline.svg'
import {
  ClockCircleOutlined,
  LoadingOutlined,
  StarFilled
} from '@ant-design/icons'
import FooterDisplay from '../components/footer'
import { useBusinessData } from '../hooks/useBusinessData'
import { useNavigate, useParams } from 'react-router-dom'
import RightDrawerTemplate from '../components/RightDrawerTemplate'
import { ImageTray } from './Restaurant/ImageTray'
import ImageSlider from './sliders/ImageSlider'
import HelpSupportButton from './HelpSupportButton'
import { storePromoImages } from '../common/promoConfig'
import RawFoodPlaceholder from '../assets/images/new_ui/raw-food-placeholder.jpg'

const BusinessDetailPage = ({ businessType }) => {
  const { id: businessId } = useParams()
  const {
    businessData,
    loading,
    error,
    refetch
  } = useBusinessData({ businessId, businessType })
  const [businessRating, setBusinessRating] = useState('')
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [userRating, setUserRating] = useState(0)
  const [feedback, setFeedback] = useState('')
  const Navigate = useNavigate()

  useEffect(() => {
    if (businessData.rating) {
      setBusinessRating(businessData.rating)
    }
  }, [businessData])

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      description: message
    })
  }

  const showAboutDrawer = () => {
    setIsDrawerVisible(true)
  }

  const closeDrawer = () => {
    setIsDrawerVisible(false)
  }

  const showRatingModal = () => {
    setIsModalVisible(true)
  }

  const closeRatingModal = () => {
    setIsModalVisible(false)
    setUserRating(0)
    setFeedback('')
  }

  const submitRating = async () => {
    try {
      // await _submit_business_rating(businessId, userRating, feedback);
      openNotificationWithIcon('success', 'Thank you for your rating!')
      closeRatingModal()
    } catch (error) {
      openNotificationWithIcon(
        'error',
        'Failed to submit rating. Please try again.'
      )
    }
  }

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />

  const getReadyTime = () => {
    const { minReadyTime } = businessData
    if (!minReadyTime) return 'NA'
    return `${parseInt(minReadyTime)} mins`
  }

  if (error) {
    return (
      <>
        <div className='min-h-screen flex items-center justify-center bg-gray-50'>
          <div className='text-center py-24'>
            <div className='text-red-500 mb-6'>
              <svg
                className='w-24 h-24 mx-auto'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-gray-900 mb-4'>
              Oops! Something went wrong
            </h3>
            <p className='text-gray-600 mb-6 max-w-md mx-auto'>
              We couldn't load the business details. Please check your
              connection and try again.
            </p>
            <div className='flex gap-4 justify-center'>
              <button
                onClick={() => Navigate(-1)}
                className='bg-gray-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-600 transition'
              >
                Go Back
              </button>
              <button
                onClick={refetch}
                className='bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition'
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  let promoImages = []
  if (businessType === 'restaurant') {
    promoImages = [
      {
        id: 1,
        tag: 'FREE DELIVERY',
        tagColor: 'green',
        title: 'Order now, we fit deliver FREE',
        subtitle: 'Fast delivery. Zero charges.',
        buttonText: 'Order Now',
        bgColor: 'bg-green-50',
        textColor: 'text-green-800',
        buttonColor: 'bg-green-500 hover:bg-green-600'
      },
      {
        id: 2,
        tag: 'BULK ORDERS',
        tagColor: 'purple',
        title: 'Big event bulk order? We dey on time!',
        subtitle: 'Reliable for events, catering, and large requests.',
        buttonText: 'Order Now',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-800',
        buttonColor: 'bg-purple-600 hover:bg-purple-700'
      },
      {
        id: 3,
        tag: 'BEST VALUE',
        tagColor: 'orange',
        title: 'Chop plenty, pay small & enjoy better life',
        subtitle: 'Affordable meals for every taste and budget.',
        buttonText: 'See Menu Deals',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-800',
        buttonColor: 'bg-orange-600 hover:bg-orange-700'
      }
    ]
  } else if (businessType === 'store' || businessType === 'raw_food') {
    promoImages = storePromoImages
  }

  const businessBanner =
    businessData?.bannerImage ||
    businessData?.business?.bannerImage ||
    (businessType === 'raw_food' ? RawFoodPlaceholder : null)

  return (
    <>
      <Spin spinning={loading} indicator={antIcon}>
        <div className='h-auto'>
          <LocationWrapperMini
            businessType={businessType}
            businessName={businessData?.name ?? businessData?.business?.name}
            businessRating={businessRating}
            onAboutClick={showAboutDrawer}
          />
          <div className='singlePageHeader hidden md:block'>
            <Row justify={'center'}>
              <Col xs={22} lg={24}>
                <div className='flex flex-col flex-wrap justify-between gap-2 lg:px-20 py-5'>
                  <h1 className='text-gray-900 text-4xl'>
                    {businessData?.name ?? businessData?.business?.name}
                  </h1>
                  <div className='flex gap-10'>
                    <div className='flex flex-col'>
                      <p className='text-[#959595]'>Profile</p>
                      <div
                        className='flex gap-3 items-center'
                        onClick={showAboutDrawer}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={briefCase} className='w-6' alt='' />
                        <p className='font-semibold underline'>About Us</p>
                      </div>
                    </div>
                    <div className='flex flex-col'>
                      <p className='text-[#959595]'>Rating</p>
                      <div className='flex gap-2 items-center'>
                        <StarFilled
                          style={{ fontSize: '15px', color: '#FF8900' }}
                        />
                        <p className='font-semibold'>
                          {loading ? 'NA' : businessRating}
                        </p>
                      </div>
                    </div>
                    <button
                      className={
                        'py-2 px-6 text-[12px] font-bold bg-gray-900 rounded-xl text-white'
                      }
                      onClick={showRatingModal}
                    >
                      Rate Us
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
          <div className='mt-2 md:mt-16 px-4 md:px-24 mb-12'>
            <div className='grid grid-cols-1 gap-4 md:gap-10'>
              <div>
                <ImageSlider images={promoImages} />
              </div>
            </div>
          </div>
          <div className='singlePageHeader'>
            <Row justify={'center'}>
              <Col xs={22} lg={24}>
                {businessBanner && (
                  <div className='lg:px-20'>
                    <div className='singleBusiness_img flex mx-auto mt-2'>
                      <img
                        src={businessBanner}
                        alt={businessData.name}
                        className='w-full h-[200px] object-cover rounded-lg'
                      />
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </div>

          <div className='lg:px-20 pt-4 md:pt-6'>
            <Row justify={'center'} className='h-full'>
              <Col xs={22} xl={16}>
                <ImageTray
                  businessType={businessType}
                  isOpen={
                    businessType === 'restaurant' ? businessData.isOpen : true
                  }
                  openTime={businessData.openTime}
                  closeTime={businessData.closeTime}
                />
              </Col>
            </Row>
          </div>
        </div>
      </Spin>
      <FooterDisplay />

      <RightDrawerTemplate
        closeDrawer={closeDrawer}
        openDrawer={isDrawerVisible}
        width={450}
        title={`About ${businessData.name ?? businessData?.business?.name}`}
      >
        <div>
          {/* <h2 className="text-green-800 font-bold flex items-center my-4 mt-8 text-5xl ">
            {businessData.name ?? businessData?.business?.name}
          </h2> */}
          <div className='singleBusiness_img flex mx-auto mt-2'>
            <img
              src={businessBanner}
              alt={businessData.name ?? businessData?.business?.name}
              className='w-full h-[250px] object-cover rounded-lg'
            />
          </div>
          <div className={'description mt-8'}>
            <p className={'my-8'}>
              {businessData.description ||
                businessData?.business?.description ||
                'No description available for this business.'}
            </p>

            <div className='flex flex-col gap-y-5'>
              {(() => {
                const rawPhone =
                  businessData?.contactTelephone ||
                  businessData?.business?.owner?.phoneNumber ||
                  businessData?.phone ||
                  "";
                const phoneCode =
                  businessData?.phoneCode ||
                  businessData?.business?.owner?.phoneCode ||
                  "+234";

                if (!rawPhone) return null;

                const cleanDigits = rawPhone.replace(/\D/g, "");
                let formattedPhone = "";
                let waDigits = "";

                if (cleanDigits.startsWith("234")) {
                  formattedPhone = `+${cleanDigits}`;
                  waDigits = cleanDigits;
                } else if (cleanDigits.startsWith("0")) {
                  const withoutZero = cleanDigits.substring(1);
                  formattedPhone = `${phoneCode} ${withoutZero}`;
                  const cleanCode = phoneCode.replace(/\D/g, "") || "234";
                  waDigits = `${cleanCode}${withoutZero}`;
                } else {
                  formattedPhone = `${phoneCode} ${cleanDigits}`;
                  const cleanCode = phoneCode.replace(/\D/g, "") || "234";
                  waDigits = `${cleanCode}${cleanDigits}`;
                }

                return (
                  <div>
                    <h2>CONTACT PHONE</h2>
                    <a
                      href={`https://wa.me/${waDigits}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center text-2xl mt-2 gap-2 text-green-600 hover:text-green-800 font-semibold'
                    >
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='currentColor'
                      >
                        <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488' />
                      </svg>
                      <span>{formattedPhone}</span>
                    </a>
                  </div>
                );
              })()}

              {businessType === 'restaurant' && (
                <>
                  <div>
                    <h2>ALLERGENS </h2>
                    <p>
                      You can call {businessData.name} to ask about their
                      ingredients and allergen information, production or
                      cooking methods.
                    </p>
                  </div>
                  <div>
                    <h2>HYGIENE RATING</h2>
                    <p>
                      Restaurant food hygiene rating is a good standing meaning
                      the kitchen has good hygiene practices.
                    </p>
                  </div>
                  <div className='restaurant_profile_note'>
                    <h2>NOTES</h2>
                    <p>
                      All dishes may contain traces of the following allergens:
                      Gluten, Crustaceans, Eggs, Fish, Peanuts, Soybeans, Milk,
                      Nuts (e.g. almonds, hazelnuts, walnuts, cashews), For any
                      questions regarding the allergen contents of specific
                      dishes please contact the restaurant directly.
                    </p>
                  </div>
                </>
              )}
              <div>
                <h2>LOCATION</h2>
                <p>
                  {`${businessData?.pickupAddressString || businessData?.address || businessData?.business?.address || ""}${
                    businessData?.pickupLga || businessData?.lga || businessData?.business?.lga
                      ? `, ${businessData?.pickupLga || businessData?.lga || businessData?.business?.lga}`
                      : ""
                  } ${businessData?.pickupState || businessData?.state || businessData?.business?.state || ""}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RightDrawerTemplate>

      <Modal
        title={`Rate ${businessData.name}`}
        open={isModalVisible}
        onCancel={closeRatingModal}
        onOk={submitRating}
        okText='Submit'
        okButtonProps={{
          style: {
            backgroundColor: '#44843F',
            borderColor: '#44843F',
            color: '#fff'
          }
        }}
      >
        <div className='flex flex-col gap-4'>
          <Rate value={userRating} onChange={value => setUserRating(value)} />
          <textarea
            className='w-full p-2 border rounded'
            placeholder='Leave your feedback...'
            rows={4}
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
          />
        </div>
      </Modal>
      <HelpSupportButton page="SERVICE" businessId={businessId} />
    </>
  )
}

export default BusinessDetailPage
